import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getStudyPipelineStatus, runDetectTmaCores } from 'api/platform';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { ActionDialogType, useActiveActionDialogType } from 'components/ActionDialog';
import { DialogChromeFooter, dialogActionButtonSx, dialogPrimaryActionSx } from 'components/atoms/Dialog/DialogChrome';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { every, filter, includes, map, size } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React, { FunctionComponent, useState } from 'react';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';

interface RunDetectTmaCoresProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
}

type DetectionMaskMode = 'dnaThreshold' | 'savedTsm';

export const RunDetectTmaCores: FunctionComponent<RunDetectTmaCoresProps> = ({ casesParams, onClose }) => {
  const [, setCurrentActionItemType] = useActiveActionDialogType();
  const [detectionMaskMode, setDetectionMaskMode] = useState<DetectionMaskMode>('dnaThreshold');
  const requestedSlideIds = casesParams.slideIdsToInclude || [];
  const hasExplicitSlideIds = size(requestedSlideIds) > 0;
  const selectedSlidesQuery = useQuery({
    queryKey: ['detectTmaCoresSelectedSlides', casesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(casesParams, signal),
    retry: false,
  });
  const selectedSlides = hasExplicitSlideIds
    ? filter(selectedSlidesQuery.data, (slide) => includes(requestedSlideIds, slide.slideId))
    : selectedSlidesQuery.data || [];
  const selectedSlideIds = map(selectedSlides, 'slideId');
  const hasSelectedSlides = size(selectedSlideIds) > 0;
  const hasAllRequestedSlides = !hasExplicitSlideIds || size(selectedSlides) === size(requestedSlideIds);
  const areSelectedSlidesMultiplex =
    hasSelectedSlides &&
    hasAllRequestedSlides &&
    every(selectedSlides, (slide) => slide.stainingType === MULTIPLEX_STAIN_ID);
  const tsmStatusQuery = useQuery({
    queryKey: ['detectTmaCoresTsmStatus', casesParams],
    queryFn: ({ signal }) => getStudyPipelineStatus(casesParams, signal),
    enabled: areSelectedSlidesMultiplex,
    retry: false,
  });
  const slidesWithTsm = map(tsmStatusQuery.data?.latestTsmArtifacts, 'slideId');
  const hasTsm = areSelectedSlidesMultiplex && every(selectedSlideIds, (slideId) => includes(slidesWithTsm, slideId));
  const isTsmStatusLoading = tsmStatusQuery.isLoading || tsmStatusQuery.isFetching;
  const canExecute =
    areSelectedSlidesMultiplex && (detectionMaskMode === 'dnaThreshold' || (!isTsmStatusLoading && hasTsm));
  const detectTmaCoresMutation = useMutationWithErrorSnackbar({
    mutationFn: runDetectTmaCores,
    mutationDescription: 'start TMA well detection',
    onSuccess: () => {
      enqueueSnackbar('TMA well detection started', { variant: 'success' });
      onClose(undefined, true);
    },
  });

  const openTissueSegmentation = () => {
    setCurrentActionItemType(ActionDialogType.MultiplexTissueSegmentationMask);
  };

  const renderStatus = () => {
    if (selectedSlidesQuery.isLoading || selectedSlidesQuery.isFetching) {
      return (
        <Alert severity="info">
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <CircularProgress size={18} />
            </Grid>
            <Grid item>
              <Typography>Checking the selected slide.</Typography>
            </Grid>
          </Grid>
        </Alert>
      );
    }

    if (selectedSlidesQuery.isError || !hasAllRequestedSlides) {
      return <Alert severity="error">The selected slide could not be checked.</Alert>;
    }

    if (!hasSelectedSlides) {
      return <Alert severity="error">Select at least one multiplex slide to detect TMA wells.</Alert>;
    }

    if (!areSelectedSlidesMultiplex) {
      return <Alert severity="error">Detect TMA wells is currently available only for multiplex slides.</Alert>;
    }

    if (isTsmStatusLoading) {
      return (
        <Alert severity="info">
          DNA-derived threshold detection is ready. Checking whether saved tissue segmentation is also available.
        </Alert>
      );
    }

    if (tsmStatusQuery.isError) {
      return (
        <Alert severity="warning">
          Saved tissue segmentation could not be checked. DNA-derived threshold detection remains available.
        </Alert>
      );
    }

    if (!hasTsm) {
      return (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" variant="outlined" onClick={openTissueSegmentation}>
              Run tissue segmentation
            </Button>
          }
        >
          No saved tissue segmentation is available for{' '}
          {size(selectedSlideIds) === 1 ? 'slide' : 'every selected slide'}
          {size(selectedSlideIds) === 1 ? ` ${selectedSlideIds[0]}` : ''}. You can use the recommended DNA-derived
          threshold mask or run tissue segmentation first.
        </Alert>
      );
    }

    return (
      <Alert severity="success">
        Saved tissue segmentation is available for {size(selectedSlideIds) === 1 ? 'slide' : 'every selected slide'}
        {size(selectedSlideIds) === 1 ? ` ${selectedSlideIds[0]}` : ''}.
      </Alert>
    );
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>{renderStatus()}</Grid>
      {areSelectedSlidesMultiplex ? (
        <Grid item>
          <FormControl>
            <FormLabel id="detect-tma-mask-mode-label">Detection mask</FormLabel>
            <RadioGroup
              aria-labelledby="detect-tma-mask-mode-label"
              value={detectionMaskMode}
              onChange={(event) => setDetectionMaskMode(event.target.value as DetectionMaskMode)}
            >
              <FormControlLabel
                value="dnaThreshold"
                control={<Radio />}
                label="DNA-derived threshold mask (recommended)"
              />
              <FormControlLabel
                value="savedTsm"
                control={<Radio />}
                label="Saved tissue segmentation mask"
                disabled={isTsmStatusLoading || tsmStatusQuery.isError || !hasTsm}
              />
            </RadioGroup>
            {isTsmStatusLoading ? (
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <CircularProgress size={16} />
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    Checking saved tissue segmentation availability.
                  </Typography>
                </Grid>
              </Grid>
            ) : null}
          </FormControl>
        </Grid>
      ) : null}
      <Grid item>
        <DialogChromeFooter sx={{ px: 0, pb: 0 }}>
          <Button color="inherit" onClick={() => onClose(undefined, true)} sx={dialogActionButtonSx}>
            Close
          </Button>
          {canExecute ? (
            <Button
              color="primary"
              variant="contained"
              disabled={detectTmaCoresMutation.isLoading}
              onClick={() =>
                detectTmaCoresMutation.mutate({
                  ...casesParams,
                  useThresholdedMifTsm: detectionMaskMode === 'dnaThreshold',
                })
              }
              sx={dialogPrimaryActionSx}
            >
              Execute
            </Button>
          ) : null}
        </DialogChromeFooter>
      </Grid>
    </Grid>
  );
};
